import type { AuthSession, SessionRefreshInfo } from './types/session';

interface SessionManagerConfig {
  /**
   * Callback to refresh an expiring session
   */
  onRefresh?: (session: AuthSession) => Promise<AuthSession | null>;

  /**
   * Time before expiry to trigger refresh (milliseconds)
   * @default 60000 (1 minute)
   */
  refreshSkewMs?: number;
}

type SessionEventMap = {
  session: (session: AuthSession | null) => void;
  sessionExpired: (session: AuthSession) => void;
  sessionRefreshed: (session: AuthSession) => void;
  sessionWillRefresh: (info: SessionRefreshInfo) => void;
};

type SessionEventKey = keyof SessionEventMap;
type SessionEventHandler<K extends SessionEventKey> = SessionEventMap[K];

export class SessionManager {
  private session: AuthSession | null = null;
  private timer: NodeJS.Timeout | null = null;
  private readonly onRefresh?: (session: AuthSession) => Promise<AuthSession | null>;
  private readonly events: Partial<Record<SessionEventKey, SessionEventHandler<any>[]>> = {};
  private readonly refreshSkewMs: number;
  private consecutiveFailures: number = 0;
  private readonly maxBackoffMs: number = 30000; // Max 30 second backoff
  private readonly baseBackoffMs: number = 2000; // Start with 2 second backoff

  constructor(config: SessionManagerConfig = {}) {
    this.onRefresh = config.onRefresh;
    this.refreshSkewMs = config.refreshSkewMs ?? 60000; // Default 60 seconds
  }

  /**
   * Set the current session and schedule refresh
   */
  setSession(session: AuthSession | null): void {
    this.session = session;
    this.schedule();
    this.emit('session', session);
  }

  /**
   * Get the current session
   */
  getSession(): AuthSession | null {
    return this.session;
  }

  /**
   * Clear the current session
   */
  clear(): void {
    this.session = null;
    this.cancel();
    this.emit('session', null);
  }

  /**
   * Subscribe to session events
   */
  on<K extends SessionEventKey>(event: K, handler: SessionEventHandler<K>): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event]!.push(handler as any);
  }

  /**
   * Unsubscribe from session events
   */
  off<K extends SessionEventKey>(event: K, handler: SessionEventHandler<K>): void {
    const handlers = this.events[event];
    if (handlers) {
      const index = handlers.indexOf(handler as any);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Destroy the session manager
   */
  destroy(): void {
    this.cancel();
    this.session = null;
    Object.keys(this.events).forEach(key => {
      delete this.events[key as SessionEventKey];
    });
  }

  private emit<K extends SessionEventKey>(
    event: K,
    payload: Parameters<SessionEventMap[K]>[0]
  ): void {
    const handlers = this.events[event];
    if (!handlers) return;

    for (const handler of handlers) {
      try {
        handler(payload as any);
      } catch (error) {
        console.error(`Error in session event handler for ${event}:`, error);
      }
    }
  }

  private cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Calculate backoff delay with exponential increase, capped at maxBackoffMs
   */
  private getBackoffDelay(): number {
    // Exponential backoff: 2s, 4s, 8s, 16s, 30s (capped)
    // consecutiveFailures is 1 after first failure, so use (n-1) as exponent
    const delay = Math.min(
      this.baseBackoffMs * Math.pow(2, Math.max(0, this.consecutiveFailures - 1)),
      this.maxBackoffMs
    );
    return delay;
  }

  /**
   * Schedule a retry after a failed refresh attempt
   */
  private scheduleRetry(session: AuthSession): void {
    this.cancel();

    const now = Date.now();
    const ttl = session.expiresAt - now;

    // If already expired, don't retry
    if (ttl <= 0) {
      this.emit('sessionExpired', session);
      return;
    }

    // Calculate backoff, but don't exceed remaining TTL
    const backoff = Math.min(this.getBackoffDelay(), ttl - 1000); // Leave 1s buffer

    if (backoff <= 0) {
      // No time left to retry, just wait for expiry
      this.timer = setTimeout(() => {
        this.emit('sessionExpired', session);
      }, ttl);
      return;
    }

    console.log(`[SessionManager] Refresh failed, retrying in ${backoff}ms (attempt ${this.consecutiveFailures + 1})`);

    this.timer = setTimeout(async () => {
      if (!this.onRefresh) {
        if (Date.now() >= session.expiresAt) {
          this.emit('sessionExpired', session);
        }
        return;
      }

      try {
        const refreshed = await this.onRefresh(session);
        // Check if we got a genuinely NEW session (different expiresAt)
        if (refreshed?.expiresAt && refreshed.authToken && refreshed.expiresAt !== session.expiresAt) {
          // Success! Reset failures and schedule next refresh
          this.consecutiveFailures = 0;
          this.setSession(refreshed);
          this.emit('sessionRefreshed', refreshed);
        } else {
          // Still failed - increment failures and retry with backoff
          this.consecutiveFailures++;
          if (Date.now() >= session.expiresAt) {
            this.emit('sessionExpired', session);
          } else {
            this.scheduleRetry(session);
          }
        }
      } catch (error) {
        console.error('Session refresh error:', error);
        this.consecutiveFailures++;
        if (Date.now() >= session.expiresAt) {
          this.emit('sessionExpired', session);
        } else {
          this.scheduleRetry(session);
        }
      }
    }, backoff);
  }

  private schedule(): void {
    this.cancel();

    const session = this.session;
    if (!session?.expiresAt) return;

    const now = Date.now();
    const ttl = session.expiresAt - now;

    if (ttl <= 0) {
      this.emit('sessionExpired', session);
      return;
    }

    // Reset consecutive failures when scheduling a fresh refresh
    this.consecutiveFailures = 0;

    const refreshIn = Math.max(0, ttl - this.refreshSkewMs);
    this.emit('sessionWillRefresh', { inMs: refreshIn, ttl });

    this.timer = setTimeout(async () => {
      if (!this.onRefresh) {
        // No refresh hook; check if expired
        if (Date.now() >= session.expiresAt) {
          this.emit('sessionExpired', session);
        }
        return;
      }

      try {
        const refreshed = await this.onRefresh(session);
        // Check if we got a genuinely NEW session (different expiresAt)
        if (refreshed?.expiresAt && refreshed.authToken && refreshed.expiresAt !== session.expiresAt) {
          // Success! Schedule next refresh
          this.consecutiveFailures = 0;
          this.setSession(refreshed);
          this.emit('sessionRefreshed', refreshed);
        } else {
          // Refresh failed - start retry with backoff
          this.consecutiveFailures++;
          if (Date.now() >= session.expiresAt) {
            this.emit('sessionExpired', session);
          } else {
            this.scheduleRetry(session);
          }
        }
      } catch (error) {
        console.error('Session refresh error:', error);
        this.consecutiveFailures++;
        if (Date.now() >= session.expiresAt) {
          this.emit('sessionExpired', session);
        } else {
          this.scheduleRetry(session);
        }
      }
    }, refreshIn);
  }
}
